import React, { useEffect, useState } from "react";
import { CalendarClock, ExternalLink } from "lucide-react";
import { api } from "./api";
import { Tableau, Cellule, Badge, Eyebrow, Section, Puces } from "./ui";

/* Les cours privés réservés sur Calendly, rangés par professeur. Lecture seule : la
   réservation reste dans Calendly (c'est là que les familles choisissent leur créneau),
   la console sert à voir qui vient, quand et avec qui. */
const quand = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("fr-CH", { weekday: "short", day: "2-digit", month: "2-digit",
    hour: "2-digit", minute: "2-digit" });
};

export default function CoursPrives() {
  const [data, setData] = useState(null);
  const [statut, setStatut] = useState("active");
  const [prof, setProf] = useState("");

  useEffect(() => {
    setData(null);
    api(`/cours-prives?statut=${statut}`).then(setData)
      .catch(() => setData({ configure: true, items: [], professeurs: [],
        message: "Lecture impossible pour le moment." }));
  }, [statut]);

  const lignes = (data?.items || [])
    .filter((i) => !prof || i.professeur === prof)
    .map((i) => ({ ...i, id: i.id }));

  return (
    <div className="space-y-6" data-testid="console-cours-prives">
      <div>
        <Eyebrow>Réservés sur Calendly</Eyebrow>
        <h1 className="font-display text-3xl tracking-tight mt-1">Cours privés</h1>
        <p className="text-xs text-[var(--muted)] mt-1">
          Les rendez-vous pris par les familles sur Calendly, par professeur. La réservation
          et l'annulation se font toujours dans Calendly : cet écran est une lecture.
        </p>
      </div>

      {data && !data.configure && (
        <div className="rounded-2xl p-5" data-testid="cours-prives-a-configurer"
          style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}>
          <div className="font-semibold text-sm mb-1" style={{ color: "#92400E" }}>
            Calendly n'est pas encore relié
          </div>
          <p className="text-xs leading-relaxed" style={{ color: "#92400E" }}>{data.message}</p>
        </div>
      )}

      {data?.configure && data.message && (
        <div className="rounded-2xl p-5" data-testid="cours-prives-erreur"
          style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}>
          <p className="text-xs leading-relaxed" style={{ color: "#991B1B" }}>{data.message}</p>
        </div>
      )}

      {data?.configure && !data.message && (
        <Section
          titre={<span>Rendez-vous <span className="text-sm text-[var(--muted)]">({lignes.length})</span></span>}
          aide="Les créneaux à venir d'abord. « Annulés » montre ce que les familles ont annulé dans Calendly."
          testid="section-cours-prives"
          action={
            <a className="btn-ghost" href="https://calendly.com/event_types/user/me" target="_blank"
              rel="noreferrer" data-testid="cours-prives-ouvrir-calendly">
              <ExternalLink size={13} /> Ouvrir Calendly
            </a>
          }
        >
          <div className="flex flex-wrap gap-3 mb-4">
            <Puces testid="cours-prives-statut" valeur={statut} onChange={setStatut}
              options={[{ valeur: "active", label: "À venir et passés" },
                { valeur: "canceled", label: "Annulés" }]} />
            {(data.professeurs || []).length > 1 && (
              <Puces testid="cours-prives-prof" valeur={prof} onChange={setProf}
                options={[{ valeur: "", label: "Tous les profs" },
                  ...data.professeurs.map((p) => ({ valeur: p, label: p }))]} />
            )}
          </div>
          <Tableau
            testid="cours-prives-table"
            entetes={["Quand", "Professeur", "Cours", "Élève", "Durée", "État"]}
            lignes={lignes}
            vide="Aucun rendez-vous Calendly pour ce filtre."
            rendu={(i) => (
              <>
                <Cellule fort>
                  <span className="inline-flex items-center gap-2">
                    <CalendarClock size={13} style={{ color: "var(--primary)" }} />
                    {quand(i.debut)}
                  </span>
                </Cellule>
                <Cellule>{i.professeur}</Cellule>
                <Cellule muet>{i.type}</Cellule>
                <Cellule>
                  {(i.eleves || []).map((e) => (
                    <div key={e.email || e.nom}>
                      {e.nom}
                      <div className="text-xs text-[var(--muted)]">
                        {e.email}{e.telephone ? ` · ${e.telephone}` : ""}
                      </div>
                    </div>
                  ))}
                  {(i.eleves || []).length === 0 && "—"}
                </Cellule>
                <Cellule muet>{i.duree_min ? `${i.duree_min} min` : "—"}</Cellule>
                <Cellule>
                  {i.statut === "canceled"
                    ? <Badge ton="rouge">annulé</Badge>
                    : i.passe ? <Badge ton="bleu">passé</Badge> : <Badge ton="vert">à venir</Badge>}
                  {i.annulation && (
                    <div className="text-xs text-[var(--muted)] mt-1">{i.annulation}</div>
                  )}
                </Cellule>
              </>
            )}
          />
        </Section>
      )}
    </div>
  );
}
